import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, KeyRound } from "lucide-react";

const empty = { name: "", username: "", password: "", school_id: "", phone: "", active: true };

export default function Counselors() {
  const [counselors, setCounselors] = useState([]);
  const [schools, setSchools] = useState([]);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);
  const [resetFor, setResetFor] = useState(null);
  const [newPw, setNewPw] = useState("");

  const load = () => {
    api.get("/counselors").then((r) => setCounselors(r.data)).catch((e) => toast.error(formatError(e)));
    api.get("/schools").then((r) => setSchools(r.data));
  };
  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (c) => { setForm({ name: c.name, username: c.username, password: "", school_id: c.school_id, phone: (c.phone || "").replace(/\D/g, "").replace(/^966/, "").slice(-9), active: c.active }); setEditId(c.id); setOpen(true); };

  const save = async () => {
    if (!form.name || !form.username || !form.school_id) { toast.error("يرجى تعبئة الاسم واسم المستخدم والمدرسة"); return; }
    if (!editId && !form.password) { toast.error("كلمة المرور مطلوبة"); return; }
    try {
      if (editId) await api.put(`/counselors/${editId}`, form);
      else await api.post("/counselors", form);
      toast.success(editId ? "تم تعديل المرشد" : "تمت إضافة المرشد");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/counselors/${id}`); toast.success("تم حذف المرشد"); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const doReset = async () => {
    if (!newPw || newPw.length < 4) { toast.error("كلمة المرور قصيرة جداً"); return; }
    try {
      await api.post(`/counselors/${resetFor.id}/reset-password`, { password: newPw });
      toast.success("تم إعادة تعيين كلمة المرور");
      setResetFor(null); setNewPw("");
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-800">المرشدون</h1>
          <p className="text-slate-400">إدارة حسابات المرشدين الطلابيين</p>
        </div>
        <Button onClick={openAdd} className="gap-2 bg-emerald-800 hover:bg-emerald-900" data-testid="add-counselor-button">
          <Plus className="h-4 w-4" /> إضافة مرشد
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-right">الاسم</TableHead>
                <TableHead className="text-right">اسم المستخدم</TableHead>
                <TableHead className="text-right">المدرسة</TableHead>
                <TableHead className="text-right">رقم الجوال</TableHead>
                <TableHead className="text-center">الحالة</TableHead>
                <TableHead className="text-center">إجراءات</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {counselors.map((c) => (
                <TableRow key={c.id} data-testid={`counselor-row-${c.id}`}>
                  <TableCell className="font-semibold text-slate-800">{c.name}</TableCell>
                  <TableCell dir="ltr" className="text-right">{c.username}</TableCell>
                  <TableCell>{c.school_name}</TableCell>
                  <TableCell dir="ltr" className="text-right font-mono text-sm">{c.phone || "—"}</TableCell>
                  <TableCell className="text-center">
                    <Badge className={c.active ? "bg-green-100 text-green-800" : "bg-red-100 text-red-700"}>
                      {c.active ? "فعّال" : "موقوف"}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex justify-center gap-1">
                      <Button variant="ghost" size="icon" onClick={() => setResetFor(c)} data-testid={`reset-counselor-${c.id}`} title="إعادة تعيين كلمة المرور">
                        <KeyRound className="h-4 w-4 text-amber-600" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => openEdit(c)} data-testid={`edit-counselor-${c.id}`}>
                        <Pencil className="h-4 w-4 text-emerald-700" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`delete-counselor-${c.id}`}>
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="text-right">
                          <AlertDialogHeader>
                            <AlertDialogTitle>حذف المرشد</AlertDialogTitle>
                            <AlertDialogDescription>هل تريد حذف حساب "{c.name}"؟</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>إلغاء</AlertDialogCancel>
                            <AlertDialogAction onClick={() => remove(c.id)} className="bg-red-600 hover:bg-red-700">حذف</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {counselors.length === 0 && (
                <TableRow><TableCell colSpan={6} className="py-12 text-center text-slate-400">لا يوجد مرشدون بعد</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/edit */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="text-right">
          <DialogHeader><DialogTitle>{editId ? "تعديل مرشد" : "إضافة مرشد"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="mb-1 block text-xs text-slate-500">الاسم *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="counselor-name" />
            </div>
            <div>
              <Label className="mb-1 block text-xs text-slate-500">اسم المستخدم *</Label>
              <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} dir="ltr" className="text-right" data-testid="counselor-username" />
            </div>
            <div>
              <Label className="mb-1 block text-xs text-slate-500">رقم الجوال</Label>
              <div className="flex items-center gap-2" dir="ltr">
                <span className="rounded-md border bg-slate-50 px-3 py-2 text-sm font-mono text-slate-600">+966</span>
                <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, "").slice(0, 9) })}
                  placeholder="5XXXXXXXX" inputMode="numeric" maxLength={9} dir="ltr"
                  className="flex-1 text-right font-mono" data-testid="counselor-phone" />
              </div>
              <p className="mt-1 text-[11px] text-slate-400">اكتب 9 أرقام بدون الصفر (مثال: 5XXXXXXXX)</p>
            </div>
            <div>
              <Label className="mb-1 block text-xs text-slate-500">{editId ? "كلمة المرور (اتركها فارغة لعدم التغيير)" : "كلمة المرور *"}</Label>
              <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} dir="ltr" className="text-right" data-testid="counselor-password" />
            </div>
            <div>
              <Label className="mb-1 block text-xs text-slate-500">المدرسة *</Label>
              <Select value={form.school_id} onValueChange={(v) => setForm({ ...form, school_id: v })}>
                <SelectTrigger data-testid="counselor-school"><SelectValue placeholder="اختر المدرسة" /></SelectTrigger>
                <SelectContent>
                  {schools.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-lg border p-3">
              <Label>حالة الحساب: {form.active ? "فعّال" : "موقوف"}</Label>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="counselor-active" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} className="bg-emerald-800 hover:bg-emerald-900" data-testid="save-counselor-button">حفظ</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password */}
      <Dialog open={!!resetFor} onOpenChange={(o) => { if (!o) { setResetFor(null); setNewPw(""); } }}>
        <DialogContent className="text-right">
          <DialogHeader><DialogTitle>إعادة تعيين كلمة المرور — {resetFor?.name}</DialogTitle></DialogHeader>
          <div>
            <Label className="mb-1 block text-xs text-slate-500">كلمة المرور الجديدة</Label>
            <Input type="text" value={newPw} onChange={(e) => setNewPw(e.target.value)} dir="ltr" className="text-right" data-testid="new-password-input" />
          </div>
          <DialogFooter>
            <Button onClick={doReset} className="bg-emerald-800 hover:bg-emerald-900" data-testid="confirm-reset-button">تعيين</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
